/**
 * Compact single-choice dropdown for mini-modal settings.
 * Styled to match {@link ImageOptionSelector} and overlay palette tokens.
 */

import { AnimatePresence, motion } from "motion/react";
import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useOverlayScrollIsolationHook } from "../../hooks/useOverlayScrollIsolationHook";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import ArrowBottomIcon from "../../image/icons/map/details/ArrowBottom.svg";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { isUiMotionEnabled } from "../shared/motion/uiMotion.tools";
import { useMiniModalScope } from "./MiniModalScopeContext";
import { miniModalAnchoredOverlayRootProps } from "./miniModal.overlay.configuration";
import {
    buildMiniModalSelectChevronTransition,
    buildMiniModalSelectListClosedState,
    buildMiniModalSelectListExitState,
    buildMiniModalSelectListExitTransition,
    buildMiniModalSelectListOpenState,
    buildMiniModalSelectListOpenTransition,
    buildMiniModalSelectOptionInitialState,
    buildMiniModalSelectOptionRevealTransition,
    buildMiniModalSelectOptionVisibleState,
    buildMiniModalSelectTriggerLabelTransition,
    MINI_MODAL_SELECT_LIST_PRESENCE_KEY,
    resolveMiniModalSelectListTransformOrigin,
} from "./miniModalSelect.animation.tools";

export interface MiniModalSelectOption {

    /** Stable option identifier used for value comparison. */
    "id": string;

    /** Primary label shown in the trigger and option row. */
    "label": string;

    /** Optional muted secondary line below the label. */
    "sublabel"?: string;

    /** Optional leading image for the option row and trigger. */
    "iconSrc"?: string;

    /** Optional custom leading decoration (e.g. transfer-count dots). */
    "decoration"?: ReactNode;

    /** Disables selection for this option when true. */
    "disabled"?: boolean;
}

interface MiniModalSelectProps {

    /** Available options. */
    "options": readonly MiniModalSelectOption[];

    /** Currently selected option id. */
    "value": string;

    /** Emits the next selected option id. */
    "onChange": ( nextValue: string ) => void;

    /** Enables dark palette variants. */
    "isDarkUi": boolean;

    /** Placeholder when no option matches `value`. */
    "placeholder"?: string;

    /** Accessible name for the combobox trigger. */
    "ariaLabel": string;

    /** Disables all interactions when true. */
    "disabled"?: boolean;

    /** Optional class names on the root wrapper. */
    "className"?: string;

    /**
     * When `true`, requests the parent {@link MiniModal} to close after a
     * successful value change. Defaults to `false`.
     */
    "dismissParentOnSelect"?: boolean;
}

const LIST_MAX_HEIGHT_PX = 220;

/** Gap between the trigger and the portaled list panel. */
const LIST_GAP_PX = 6;

/** Minimum viewport padding when clamping the portaled list. */
const VIEWPORT_PADDING_PX = 8;

/** Fixed overlay z-index: one step above {@link MiniModal}. */
const LIST_PORTAL_Z_INDEX = 2147483647;

interface MiniModalSelectListPosition {

    /** Left offset in viewport coordinates. */
    "left": number;

    /** Panel width aligned with the trigger. */
    "width": number;

    /** Maximum list height after viewport clamping. */
    "maxHeight": number;

    /** When true, `top` anchors the panel below the trigger. */
    "opensBelow": boolean;

    /** Top offset when {@link MiniModalSelectListPosition.opensBelow} is true. */
    "top"?: number;

    /** Bottom offset when the panel opens above the trigger. */
    "bottom"?: number;
}

/**
 * Computes viewport-fixed coordinates for the portaled list panel.
 *
 * @param triggerRect Trigger bounding box in viewport space.
 * @returns Clamped list geometry, flipping above when needed.
 */
const computeMiniModalSelectListPosition = ( triggerRect: DOMRect ): MiniModalSelectListPosition => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const width = triggerRect.width;
    const maxLeft = Math.max( VIEWPORT_PADDING_PX, viewportWidth - width - VIEWPORT_PADDING_PX );
    const left = Math.max( VIEWPORT_PADDING_PX, Math.min( triggerRect.left, maxLeft ) );

    const spaceBelow = viewportHeight - triggerRect.bottom - LIST_GAP_PX - VIEWPORT_PADDING_PX;
    const spaceAbove = triggerRect.top - LIST_GAP_PX - VIEWPORT_PADDING_PX;
    const opensBelow = spaceBelow >= spaceAbove;
    const availableSpace = opensBelow ? spaceBelow : spaceAbove;
    const maxHeight = Math.min( LIST_MAX_HEIGHT_PX, Math.max( availableSpace, 64 ) );

    if ( opensBelow ) {
        return {
            left,
            width,
            maxHeight,
            opensBelow,
            "top": triggerRect.bottom + LIST_GAP_PX,
        };
    }

    return {
        left,
        width,
        maxHeight,
        opensBelow,
        "bottom": viewportHeight - triggerRect.top + LIST_GAP_PX,
    };
};

/**
 * Renders optional leading visuals for an option row.
 *
 * @param option Option metadata.
 * @returns Leading decoration node or `null`.
 */
const renderOptionLeading = ( option: MiniModalSelectOption ): ReactNode => {
    if ( option.decoration !== undefined ) {
        return (
            <span
                aria-hidden="true"
                className="flex shrink-0 items-center justify-center w-8 h-8"
            >
                {option.decoration}
            </span>
        );
    }

    if ( option.iconSrc !== undefined && option.iconSrc.length > 0 ) {
        return (
            <img
                alt=""
                className="block h-7 w-7 shrink-0 object-contain"
                loading="lazy"
                src={option.iconSrc}
            />
        );
    }

    return null;
};

/**
 * Single-choice dropdown aligned with mini-modal overlay styling.
 *
 * @param props Select options, value, and change handler.
 * @returns Styled combobox with animated list panel.
 */
const MiniModalSelect: React.FC<MiniModalSelectProps> = ( {
    options,
    value,
    onChange,
    isDarkUi,
    placeholder = "Choisir…",
    ariaLabel,
    disabled = false,
    className = "",
    dismissParentOnSelect = false,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const miniModalScope = useMiniModalScope();
    const listboxId = useId();
    const wrapperRef = useRef<HTMLDivElement>( null );
    const triggerRef = useRef<HTMLButtonElement>( null );
    const listRef = useRef<HTMLDivElement>( null );
    const listScrollRef = useRef<HTMLDivElement>( null );
    const [ isOpen, setIsOpen ] = useState( false );
    const [ highlightedIndex, setHighlightedIndex ] = useState<number>( -1 );
    const [ listPosition, setListPosition ] = useState<MiniModalSelectListPosition | null>( null );

    useOverlayScrollIsolationHook( listScrollRef, { "isEnabled": isOpen } );

    const selectedOption = useMemo( (): MiniModalSelectOption | undefined => {
        return options.find( ( option ) => option.id === value );
    }, [ options, value ] );

    const enabledOptions = useMemo( (): readonly MiniModalSelectOption[] => {
        return options.filter( ( option ) => option.disabled !== true );
    }, [ options ] );

    const closeList = useCallback( (): void => {
        setIsOpen( false );
        setHighlightedIndex( -1 );

        if ( !animationsEnabled ) {
            setListPosition( null );
        }
    }, [ animationsEnabled ] );

    /** Clears portaled geometry once the list exit animation has finished. */
    const handleListExitComplete = useCallback( (): void => {
        setListPosition( null );
    }, [] );

    /**
     * Syncs the portaled list position with the trigger geometry.
     *
     * @returns `true` when the trigger is measurable.
     */
    const syncListPositionFromTrigger = useCallback( (): boolean => {
        const triggerElement = triggerRef.current;

        if ( triggerElement === null ) {
            return false;
        }

        setListPosition( computeMiniModalSelectListPosition( triggerElement.getBoundingClientRect() ) );

        return true;
    }, [] );

    const openList = useCallback( (): void => {
        if ( disabled ) {
            return;
        }

        if ( !syncListPositionFromTrigger() ) {
            return;
        }

        const selectedIndex = enabledOptions.findIndex( ( option ) => option.id === value );
        setHighlightedIndex( selectedIndex >= 0 ? selectedIndex : 0 );
        setIsOpen( true );
    }, [ disabled, enabledOptions, syncListPositionFromTrigger, value ] );

    const toggleList = useCallback( (): void => {
        if ( isOpen ) {
            closeList();

            return;
        }

        openList();
    }, [ closeList, isOpen, openList ] );

    const selectOption = useCallback( ( optionId: string ): void => {
        onChange( optionId );
        closeList();

        if ( dismissParentOnSelect ) {
            miniModalScope?.requestClose();
        }
    }, [ closeList, dismissParentOnSelect, miniModalScope, onChange ] );

    const handleTriggerKeyDown = useCallback( ( event: KeyboardEvent<HTMLButtonElement> ): void => {
        if ( disabled ) {
            return;
        }

        if ( event.key === "Enter" || event.key === " " ) {
            event.preventDefault();
            toggleList();

            return;
        }

        if ( event.key === "ArrowDown" || event.key === "ArrowUp" ) {
            event.preventDefault();

            if ( !isOpen ) {
                openList();

                return;
            }

            if ( enabledOptions.length === 0 ) {
                return;
            }

            setHighlightedIndex( ( currentIndex ) => {
                if ( currentIndex < 0 ) {
                    return event.key === "ArrowDown" ? 0 : enabledOptions.length - 1;
                }

                if ( event.key === "ArrowDown" ) {
                    return ( currentIndex + 1 ) % enabledOptions.length;
                }

                return ( currentIndex - 1 + enabledOptions.length ) % enabledOptions.length;
            } );
        }
    }, [ disabled, enabledOptions.length, isOpen, openList, toggleList ] );

    const handleListKeyDown = useCallback( ( event: KeyboardEvent<HTMLDivElement> ): void => {
        if ( event.key === "Escape" ) {
            event.preventDefault();
            event.stopPropagation();
            closeList();

            return;
        }

        if ( event.key === "Enter" && highlightedIndex >= 0 ) {
            const option = enabledOptions[highlightedIndex];

            if ( option !== undefined ) {
                event.preventDefault();
                selectOption( option.id );
            }
        }
    }, [ closeList, enabledOptions, highlightedIndex, selectOption ] );

    useEffect( () => {
        if ( !isOpen ) {
            return;
        }

        const handleViewportChange = ( event: Event ): void => {
            const target = event.target;

            if ( target instanceof Node && listScrollRef.current?.contains( target ) === true ) {
                return;
            }

            if ( !syncListPositionFromTrigger() ) {
                closeList();
            }
        };

        window.addEventListener( "resize", handleViewportChange );
        window.addEventListener( "scroll", handleViewportChange, true );

        return () => {
            window.removeEventListener( "resize", handleViewportChange );
            window.removeEventListener( "scroll", handleViewportChange, true );
        };
    }, [ closeList, isOpen, syncListPositionFromTrigger ] );

    useEffect( () => {
        if ( !isOpen ) {
            return;
        }

        const handlePointerDownOutside = ( event: MouseEvent ): void => {
            const target = event.target;

            if ( !( target instanceof Node ) ) {
                return;
            }

            if ( wrapperRef.current?.contains( target ) === true ) {
                return;
            }

            if ( listRef.current?.contains( target ) === true ) {
                return;
            }

            closeList();
        };

        document.addEventListener( "mousedown", handlePointerDownOutside );

        return () => {
            document.removeEventListener( "mousedown", handlePointerDownOutside );
        };
    }, [ closeList, isOpen ] );

    const panelStyle: CSSProperties = useMemo( () => {
        return {
            "borderColor":     palette.overlayBorder,
            "backgroundColor": palette.overlayCardBackgroundStrong,
            "boxShadow":       "0 12px 28px rgba(15, 23, 42, 0.32)",
        };
    }, [ palette.overlayBorder, palette.overlayCardBackgroundStrong ] );

    useEffect( () => {
        if ( !isOpen || highlightedIndex < 0 || listRef.current === null ) {
            return;
        }

        const highlightedElement = listRef.current.querySelector<HTMLElement>( `[data-option-index="${ highlightedIndex }"]` );

        highlightedElement?.scrollIntoView( { "block": "nearest" } );
    }, [ highlightedIndex, isOpen ] );

    const listPanelStyle: CSSProperties | null = useMemo( () => {
        if ( listPosition === null ) {
            return null;
        }

        return {
            ...panelStyle,
            "position":  "fixed",
            "left":      listPosition.left,
            "width":     listPosition.width,
            "maxHeight": `${ listPosition.maxHeight }px`,
            "zIndex":    LIST_PORTAL_Z_INDEX,
            ...listPosition.opensBelow
                ? { "top": `${ listPosition.top }px` }
                : { "bottom": `${ listPosition.bottom }px` },
        };
    }, [ listPosition, panelStyle ] );

    const listPanel = listPosition !== null && listPanelStyle !== null
        ? createPortal(
            <AnimatePresence onExitComplete={handleListExitComplete}>
                {isOpen
                    ? (
                        <motion.div
                            {...miniModalAnchoredOverlayRootProps}
                            animate={animationsEnabled
                                ? buildMiniModalSelectListOpenState()
                                : undefined}
                            className="pointer-events-auto overflow-hidden rounded-lg border backdrop-blur-sm"
                            exit={animationsEnabled
                                ? {
                                    ...buildMiniModalSelectListExitState( listPosition.opensBelow ),
                                    "transition": buildMiniModalSelectListExitTransition( reducedMotion ),
                                }
                                : undefined}
                            id={listboxId}
                            initial={animationsEnabled
                                ? buildMiniModalSelectListClosedState( listPosition.opensBelow )
                                : false}
                            key={MINI_MODAL_SELECT_LIST_PRESENCE_KEY}
                            onKeyDown={handleListKeyDown}
                            ref={listRef}
                            role="listbox"
                            style={{
                                ...listPanelStyle,
                                "transformOrigin": resolveMiniModalSelectListTransformOrigin( listPosition.opensBelow ),
                                "willChange":      animationsEnabled ? "transform, opacity, filter" : undefined,
                            }}
                            tabIndex={-1}
                            transition={buildMiniModalSelectListOpenTransition( reducedMotion )}
                        >
                            <div
                                className={
                                    "overflow-y-auto overscroll-contain touch-pan-y py-1 " +
                                    "[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                                }
                                ref={listScrollRef}
                                style={{ "maxHeight": `${ listPosition.maxHeight }px` }}
                            >
                                {options.map( ( option, optionIndex ) => {
                                    const isSelected = option.id === value;
                                    const isHighlighted = enabledOptions[highlightedIndex]?.id === option.id;
                                    const isDisabled = option.disabled === true;
                                    const enabledIndex = enabledOptions.findIndex( ( item ) => item.id === option.id );

                                    return (
                                        <motion.button
                                            animate={animationsEnabled
                                                ? {
                                                    ...buildMiniModalSelectOptionVisibleState(),
                                                    "backgroundColor": isHighlighted || isSelected
                                                        ? palette.selectorActiveBackgroundStrong
                                                        : "transparent",
                                                }
                                                : {
                                                    "backgroundColor": isHighlighted || isSelected
                                                        ? palette.selectorActiveBackgroundStrong
                                                        : "transparent",
                                                }}
                                            aria-selected={isSelected}
                                            className={
                                                "group relative flex w-full items-center gap-2 px-2.5 py-2 text-left " +
                                                "focus-visible:outline-none " +
                                                `${ isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer" }`
                                            }
                                            data-option-index={enabledIndex}
                                            disabled={isDisabled}
                                            initial={animationsEnabled
                                                ? buildMiniModalSelectOptionInitialState()
                                                : false}
                                            key={option.id}
                                            onClick={() => {
                                                if ( !isDisabled ) {
                                                    selectOption( option.id );
                                                }
                                            }}
                                            onMouseEnter={() => {
                                                if ( !isDisabled && enabledIndex >= 0 ) {
                                                    setHighlightedIndex( enabledIndex );
                                                }
                                            }}
                                            role="option"
                                            transition={animationsEnabled
                                                ? {
                                                    ...buildMiniModalSelectOptionRevealTransition(
                                                        optionIndex,
                                                        reducedMotion,
                                                    ),
                                                    "backgroundColor": { "duration": 0.12 },
                                                }
                                                : undefined}
                                            type="button"
                                            whileTap={animationsEnabled && !isDisabled ? { "scale": 0.985 } : undefined}
                                        >
                                            <motion.span
                                                animate={{
                                                    "opacity": isSelected ? 1 : 0,
                                                    "scaleY":  isSelected ? 1 : 0.35,
                                                }}
                                                aria-hidden="true"
                                                className="h-8 w-0.5 shrink-0 rounded-full origin-center"
                                                style={{ "backgroundColor": palette.brandPrimary }}
                                                transition={buildMiniModalSelectListOpenTransition( reducedMotion )}
                                            />
                                            {renderOptionLeading( option )}
                                            <span className="min-w-0 flex-1">
                                                <span
                                                    className="block truncate text-sm font-medium leading-tight"
                                                    style={{
                                                        "color": isSelected
                                                            ? palette.selectorActiveText
                                                            : palette.overlayTextPrimary,
                                                    }}
                                                >
                                                    {option.label}
                                                </span>
                                                {option.sublabel !== undefined && option.sublabel.length > 0 && (
                                                    <span
                                                        className="mt-0.5 block truncate text-[11px] leading-tight"
                                                        style={{ "color": palette.overlayTextSecondary }}
                                                    >
                                                        {option.sublabel}
                                                    </span>
                                                )}
                                            </span>
                                            <AnimatePresence>
                                                {isSelected && (
                                                    <motion.span
                                                        animate={{
 "opacity": 1,
"scale":   1,
}}
                                                        aria-hidden="true"
                                                        className="shrink-0 text-[10px] font-bold uppercase tracking-wider"
                                                        exit={{
 "opacity": 0,
"scale":   0.75,
}}
                                                        initial={{
 "opacity": 0,
"scale":   0.65,
}}
                                                        style={{ "color": palette.brandPrimary }}
                                                        transition={buildMiniModalSelectTriggerLabelTransition( reducedMotion )}
                                                    >
                                                        ✓
                                                    </motion.span>
                                                )}
                                            </AnimatePresence>
                                        </motion.button>
                                    );
                                } )}
                            </div>
                        </motion.div>
                    )
                    : null}
            </AnimatePresence>,
            document.body,
        )
        : null;

    return (
        <div
            className={`relative w-full ${ className }`}
            ref={wrapperRef}
        >
            <motion.button
                animate={animationsEnabled
                    ? {
                        "backgroundColor": isOpen
                            ? palette.selectorActiveBackground
                            : palette.selectorIdleBackground,
                    }
                    : undefined}
                aria-controls={listboxId}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-label={ariaLabel}
                className={
                    "flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left " +
                    "hover:opacity-95 focus-visible:outline-none " +
                    "focus-visible:ring-2 focus-visible:ring-emerald-400/70"
                }
                disabled={disabled}
                onClick={toggleList}
                onKeyDown={handleTriggerKeyDown}
                ref={triggerRef}
                style={{
                    /*
                     * Soft hairline on the closed trigger only (not the dropdown list).
                     * Shorthand avoids Tailwind `border` + Motion stealing `borderColor`.
                     */
                    "border":          `1px solid ${ palette.overlayBorder }`,
                    "backgroundColor": isOpen
                        ? palette.selectorActiveBackground
                        : palette.selectorIdleBackground,
                    "color":   palette.selectorIdleText,
                    "opacity": disabled ? 0.65 : 1,
                }}
                transition={buildMiniModalSelectListOpenTransition( reducedMotion )}
                type="button"
                whileTap={animationsEnabled && !disabled ? { "scale": 0.985 } : undefined}
            >
                {selectedOption !== undefined && renderOptionLeading( selectedOption )}
                <span className="min-w-0 flex-1 overflow-hidden">
                    <AnimatePresence mode="wait">
                        <motion.span
                            animate={{
 "opacity": 1,
"y":       0,
}}
                            className="block"
                            exit={{
 "opacity": 0,
"y":       -5,
}}
                            initial={{
 "opacity": 0,
"y":       6,
}}
                            key={selectedOption?.id ?? "placeholder"}
                            transition={buildMiniModalSelectTriggerLabelTransition( reducedMotion )}
                        >
                            <span
                                className="block truncate text-sm font-medium leading-tight"
                                style={{ "color": palette.overlayTextPrimary }}
                            >
                                {selectedOption?.label ?? placeholder}
                            </span>
                            {selectedOption?.sublabel !== undefined && selectedOption.sublabel.length > 0 && (
                                <span
                                    className="mt-0.5 block truncate text-[11px] leading-tight"
                                    style={{ "color": palette.overlayTextSecondary }}
                                >
                                    {selectedOption.sublabel}
                                </span>
                            )}
                        </motion.span>
                    </AnimatePresence>
                </span>
                <motion.img
                    alt=""
                    animate={{ "rotate": isOpen ? 180 : 0 }}
                    aria-hidden="true"
                    className="h-3.5 w-3.5 shrink-0"
                    src={ArrowBottomIcon}
                    style={{ "filter": isDarkUi ? "brightness(1.4)" : undefined }}
                    transition={buildMiniModalSelectChevronTransition( reducedMotion )}
                />
            </motion.button>
            {listPanel}
        </div>
    );
};

export default MiniModalSelect;
